import React, { useState, useEffect } from 'react'
import { s3Static } from '../../../../utils/constants'
import Image from 'next/image'
import { AnimatePresence } from 'framer-motion'
import { Services } from '../../service/Services'
import { RequestProvider } from '../../../../context/RequestContext'
import { useRouter } from 'next/router'
import { Faq } from '../../../commons/Faq'
import { electricianSingaporeFaqs } from '../../../../utils/faq'
import { openModal } from '../../../../service/modal-service'
import { HandymanBooking } from './HandymanBooking'
import { WhyUs } from '@/commons/service-hub/WhyUs'
import { ReviewsSection } from '@/commons/service-hub/ReviewsSection'
import { RewardsSection } from '@/commons/service-hub/RewardsSection'
import { technicianReasons } from 'data/service-hub-content'
import { HandymanDealItems } from './HandymanDealItems'
import { ElectricianServices } from './ElectricianServices'
import { PriceList } from '@/commons/service-hub/PriceList'
import { HowToBook } from '@/commons/service-hub/HowToBook'
import { StickyCta } from '@/commons/service-hub/StickyCta'
import { AreasServed, RelatedLinks, technicianAreasIntro } from '@/commons/service-hub/RelatedLinks'
import { findItems, formatPrice, fromPrice } from '../../../../utils/handyman-price-data'
import {
    ELECTRICIAN_HUB,
    ELECTRICIAN_PRICE_PAGE,
    HANDYMAN_HUB,
    PLUMBER_HUB,
    handymanJobPages,
    jobPagePath,
    jobPagesForHub,
} from '../../../../data/handyman-job-pages'

// The "popular prices" teaser. The full list lives on the price page, so the
// hub and the price page do not compete for "electrician price" queries.
const POPULAR_ITEMS = [
    'Replace light bulb',
    'Replace power socket or switch',
    'Install/replace LED light',
    'Install ceiling fan',
    'Supply and install 13AMP power point (non-conceal)',
    'Replace circuit breaker',
    'Troubleshoot and repair faulty wiring',
    'Supply and install 15AMP aircon point (non-conceal)',
    'Install electrical distribution board (DB box)',
]

const relatedLinks = [
    ...jobPagesForHub(ELECTRICIAN_HUB).map((slug) => ({ href: jobPagePath(slug), label: handymanJobPages[slug].breadcrumbTitle })),
    { href: ELECTRICIAN_PRICE_PAGE, label: 'Electrician Price List', desc: 'Every electrical job and price' },
    { href: HANDYMAN_HUB, label: 'Handyman Services', desc: 'Mounting, curtains, locks and more' },
    { href: PLUMBER_HUB, label: 'Plumber Services', desc: 'Leaks, taps, toilets and heaters' },
]

export const Electrician = (props) => {
    const requestCategorySettings = props.requestCategorySettings;
    const countryName = props.countryName
    const testimonials = props.testimonials
    const handymanType = props.handymanType
    const handymanPriceList = props.handymanPriceList
    const requestType = 'Handyman Services'

    const router = useRouter()

    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isShowDealItems, setIsShowDealItems ] = useState(false)
    const [ requestForm, setRequestForm ] = useState(false)

    // Handyman bookings always start from the priced job picker, never the
    // question wizard (which lacks most jobs and their details). A
    // ?requestForm=true link opens the picker.
    useEffect(() => {
        if (router.query.requestForm) setIsShowDealItems(true)
    }, [router.query.requestForm])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    const electricalItems = (handymanPriceList || []).filter(item => item.category === 'Electrical')
    const popularItems = findItems(handymanPriceList, POPULAR_ITEMS, 'electrician hub')
    const lowest = fromPrice(electricalItems)
    const priceOf = (itemName) => {
        const cost = handymanPriceList?.find(item => item.itemName === itemName)?.serviceCost
        return cost != null ? formatPrice(cost) : null
    }
    const lightingFrom = priceOf('Install/replace LED light')
    const breakerFrom = priceOf('Replace circuit breaker')
    const wiringFrom = priceOf('Troubleshoot and repair faulty wiring')

  return (
    <>
        <RequestProvider>
            <section id="electrician-singapore" className='relative'>
                {/* One image. It was rendered twice (mobile + desktop), both with
                    priority, so the LCP image was requested twice. */}
                <div className="-mt-14 relative w-full aspect-21/9 sm:aspect-auto sm:h-180 overflow-hidden">
                    <Image
                        src={s3Static + "handyman/electrician-installing-lights.jpg"}
                        fill
                        className="object-cover"
                        alt="Licensed electrician installing lights in Singapore - StringsSG"
                        priority
                        sizes="100vw"
                    />
                </div>
                <div id="book" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2 scroll-mt-20'>
                    <HandymanBooking requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} handymanType={handymanType}
                        setIsQuickBook={setIsQuickBook} countryName={countryName} setIsShowDealItems={setIsShowDealItems}
                        defaultServiceType="Electrical"
                        allowedServiceTypes={["Electrical"]}
                        handymanPriceList={handymanPriceList}
                        title="Licensed Electrician in Singapore: Same-Day, Fixed Prices"
                        subtitle={`Power trips, wiring, lights, fans and DB boxes${lowest != null ? ` from ${formatPrice(lowest)}` : ''}. Electricians island-wide, 8am to 10pm daily.`}/>
                </div>
            </section>
            {isShowDealItems &&
                <AnimatePresence>
                    <HandymanDealItems requestCategorySettings={requestCategorySettings} handymanPriceList={handymanPriceList} setRequestForm={setRequestForm} setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} setIsShowDealItems={setIsShowDealItems}
                        allowedCategories={["Electrical"]}/>
                </AnimatePresence>
            }
            {requestForm &&
                <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} asSheet/>
            }
        </RequestProvider>

        <PriceList
            items={popularItems}
            title="Popular Electrician Prices in Singapore"
            subtitle="Fixed prices per job. You see the total before you confirm, with no hourly billing."
            moreHref={ELECTRICIAN_PRICE_PAGE}
            moreLabel="See the full electrician price list"
        />

        <section className='max-w-5xl mx-auto py-8 px-2'>
            <div className='max-w-3xl mx-auto'>
                <header className='space-y-4 px-2'>
                    <h2 className='text-2xl md:text-3xl text-center'>
                        <strong>Affordable, Licensed Electricians Near You</strong>
                    </h2>
                    <div className='space-y-3 text-sm leading-relaxed text-gray-600'>
                        <p>
                            StringsSG offers fast, reliable <strong>electrician services in Singapore</strong>, from power trips and electrical wiring repairs to circuit breaker replacement, ceiling fan installation, LED lighting and new power points.
                        </p>
                        {(lightingFrom || breakerFrom || wiringFrom) &&
                            <p>
                                Every job is quoted upfront at a fixed price before you confirm, so there are no hourly surprises.
                                {lightingFrom && <> LED light installation starts at {lightingFrom}</>}
                                {breakerFrom && <>, circuit breaker replacement at {breakerFrom}</>}
                                {wiringFrom && <> and electrical wiring repair at {wiringFrom}</>}.
                            </p>
                        }
                        <p>
                            We work across HDB flats, BTO and resale units, condominiums and commercial premises island-wide, with
                            <strong> same-day slots</strong> available for power failures and unsafe wiring.
                        </p>
                    </div>
                </header>
            </div>
            <h2 className='text-2xl md:text-3xl text-center font-bold pt-6 pb-4'>
                Our Electrician Services in Singapore
            </h2>
            <ElectricianServices handymanPriceList={handymanPriceList}/>
        </section>

        <HowToBook serviceLabel="electrician"/>
        <WhyUs title="Why Choose StringsSG Electrician Services?" reasons={technicianReasons('Electrician')}/>

        <ReviewsSection
            title="What Customers Say About Our Electrician Services"
            intro={<>Real feedback from homeowners and businesses who trust <strong>StringsSG for electrical service in Singapore</strong>.</>}
            testimonials={testimonials} />

        <AreasServed title="Electrician Near You, Island-Wide" intro={technicianAreasIntro('electrician')}/>

        <RewardsSection serviceNoun="electrician" />
        <section className='max-w-5xl mx-auto py-10'>
            <Faq
                title="Electrician FAQs"
                questionsAnswers={electricianSingaporeFaqs}
            />
        </section>
        <RelatedLinks title="Electrical Jobs and Prices" links={relatedLinks}/>
        {!requestForm && !isShowDealItems &&
            <StickyCta onBook={() => setIsShowDealItems(true)} whatsAppText="Hi, I'd like to book an electrician."/>
        }
    </>
  )
}
